import React, { ComponentProps, useEffect } from 'react';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { Form, SonForm, SubmitButton, useForm, Switch, CheckBoxParam } from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { CollectionParam, createCollectionName } from '../../../_lib/Form2/types/CollectionParam';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { SwitchModifier } from '../../../_lib/Form2/elements/Switch/Switch';
import { Form2Modifier } from '../../../_lib/Form2/Form';

type AvailableParam = CheckBoxParam<'available1' | 'available2'>;
type GroupParam = CollectionParam<'groups', AvailableParam>;

type RoleSpecificAreaGroupsEditProps = {
  form: SonForm<GroupParam>;
};

export function RoleSpecificAreaGroupsEdit({ form }: RoleSpecificAreaGroupsEditProps) {
  const { t } = useSonTranslation('AreaGroups');
  const uId = useHtmlValidId();

  const { delayedRefresh } = useRefreshBlock();

  const { formProps, fetchState, params } = useForm({
    form,
  });

  const groups: GroupParam = params.getByName('groups');

  useEffect(() => {
    if (fetchState.type === 'success') {
      return delayedRefresh();
    }
  }, [delayedRefresh, fetchState.type]);

  const getRoleGroupsByOrder = (items: Array<Array<AvailableParam>>) => {
    return items.map((item: Array<AvailableParam>, index: number) => {
      return (
        <ContentListItem key={item[0].value}>
          {item.map((param: AvailableParam) => {
            const name = createCollectionName(groups.name, index, param.name);
            if (param.type === 'checkbox') {
              return (
                <Switch
                  modifierLabel={SwitchModifier.POSITION_INHERIT}
                  key={param.value + name}
                  label={param.displayName!}
                  param={param}
                  name={name}
                />
              );
            }
            return <input key={param.value + name} type="hidden" name={name} value={param.value} />;
          })}
        </ContentListItem>
      );
    });
  };

  const id = `role-specific-area-group-edit-view-${uId}`;

  return (
    <Form id={id} {...formProps} modifier={Form2Modifier.NO_PADDING}>
      <div className="role-specific-area-groups__role-groups">
        <ContentList>{getRoleGroupsByOrder(groups.items)}</ContentList>
        <div className="role-specific-area-groups__submit">
          <SubmitButton
            buttonLabel={t('phrases:save')}
            messageSuccess={t('roleSpecificAreaGroups.views.edit.form.status.success')}
            messageSending={t('roleSpecificAreaGroups.views.edit.form.status.sending')}
            messageFail={t('roleSpecificAreaGroups.views.edit.form.status.fail')}
            fetchState={fetchState}
          />
        </div>
      </div>
    </Form>
  );
}

export const RoleSpecificAreaGroupsEditView = asFetchableView(RoleSpecificAreaGroupsEdit);
export type RoleSpecificAreaGroupsEditView = ComponentProps<typeof RoleSpecificAreaGroupsEditView>;
